"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { Button } from "../ui/button";
import FadeInUp from "@/components/common/ScrollReveal";
import Link from "next/link";
import { normalizeBlogPost } from "@/types/blogTypes";
import { useBlogStore } from "@/zustand/blogStore";
import { getCustomBlogs } from "@/services/api/customBlogApi";
import type { RawBlogPost, WPBlogPost } from "@/types/blogTypes";

function isExternalUrl(src?: string): boolean {
  if (!src) return false;
  return src.startsWith("http://") || src.startsWith("https://");
}

export default function CompanyNews() {
  const [blogData, setBlogData] = useState<(RawBlogPost | WPBlogPost)[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { setBlogId } = useBlogStore();

  useEffect(() => {
    let cancelled = false;
    getCustomBlogs(1, 10)
      .then((res) => {
        if (!cancelled && res.results?.length) {
          setBlogData(res.results);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Transform API data to BlogPost format and get first 3 blogs
  const blogs = useMemo(() => {
    if (!blogData || !Array.isArray(blogData)) return [];
    return blogData.map(normalizeBlogPost).slice(0, 3);
  }, [blogData]);

  const [mainBlog, secondBlog, thirdBlog] = blogs;

  // Hide section if loading, error, or no blogs available
  if (isLoading || error || !blogs || blogs.length === 0) {
    return null;
  }

  return (
    <FadeInUp effect="fadeInUp">
      <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto bg-[#F6F6F6] rounded-mob-10 sm:rounded-tab-10 lg:rounded-web-10 px-mob-20 sm:px-tab-20 lg:px-web-100 py-mob-40 sm:py-tab-20 lg:py-web-73">
        <h4 className="text-mob-28 sm:text-tab-28 lg:text-web-50 leading-[120%] font-medium lg:font-normal text-[#393939] capitalize text-center mb-mob-31 sm:mb-tab-31 lg:mb-web-48">
          News and Blogs
        </h4>
        <div className="lg:flex lg:gap-web-40 lg:pb-web-63 lg:border-b lg:border-[#BFBFBF] lg:mb-web-65">
          {/* recent blog - Main Blog */}
          {mainBlog && (
            <Link
              href={mainBlog.link}
              className="mb-mob-36 sm:mb-tab-36 lg:mb-0 lg:basis-[44.79vw] lg:min-w-[44.79vw] lg:max-w-[44.79vw] block"
              onClick={() => setBlogId(mainBlog.id)}
            >
              <div className="rounded-mob-6 sm:rounded-tab-6 lg:rounded-web-6 overflow-hidden mb-mob-17 sm:mb-tab-17 lg:mb-web-22 sm:h-[40vw] lg:h-[28.125vw] relative">
                <Image
                  src={mainBlog.image?.blogImage || '/images/company-news-banner-one.webp'}
                  alt={mainBlog.image?.alt || mainBlog.title}
                  fill
                  className="!static object-cover object-top"
                  unoptimized={isExternalUrl(mainBlog.image?.blogImage)}
                />
              </div>
              <span className="block text-mob-13 sm:text-tab-13 lg:text-web-18 leading-[150%] tracking-[-0.033vw] sm:tracking-[-0.025vw]  lg:tracking-[0.01vw] text-[#323232 mb-mob-6 sm:mb-tam-6 lg:mb-web-15">
                {mainBlog.date}
              </span>
              <p
                className="text-[#2E2E2E] text-mob-18 sm:text-tab-18 lg:text-web-24 leading-[140%] mb-mob-15 sm:mb-tab-15 lg:mb-web-15 block line-clamp-3"
                dangerouslySetInnerHTML={{ __html: mainBlog.title }}
              />
            </Link>
          )}

          {/* sample blog list card two */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-mob-42 sm:gap-tab-36 lg:gap-web-40 pb-mob-74 sm:pb-tab-52 lg:pb-0 border-b lg:border-none border-[#BFBFBF] mb-mob-55 sm:mb-tab-35 lg:mb-0">
            {/* list item - Second Blog */}
            {secondBlog && (
              <Link
                href={secondBlog.link}
                className="relative flex-1 rounded-mob-6 sm:rounded-tab-6 lg:rounded-web-6 overflow-hidden block"
                onClick={() => setBlogId(secondBlog.id)}
              >
                <div className="lg:max-h-[18vw] relative">
                  <Image
                    src={secondBlog.image?.blogImage || '/images/company-news-banner-two.webp'}
                    alt={secondBlog.image?.alt || secondBlog.title}
                    fill
                    className="!static "
                    unoptimized={isExternalUrl(secondBlog.image?.blogImage)}
                  />
                </div>
                <div className="absolute z-[5] w-full py-mob-14 sm:py-tab-16 lg:py-web-20 pl-mob-16 sm:pl-tab-16 lg:pl-web-22 pr-mob-32 sm:pr-tab-32 lg:pr-web-80 bottom-0 left-0 after:left-0 after:bottom-0 after:content-[''] after:absolute  bg-gradient-to-b from-transparent via-black/70 to-black after:z-[1] after:w-full after:h-full ">
                  <p
                    className="text-White text-mob-15 sm:text-tab-15 lg:text-web-22 font-medium lg:font-normal leading-[120%] relative z-[10] line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: secondBlog.title }}
                  />
                </div>
              </Link>
            )}
            {/* list item - Third Blog */}
            {thirdBlog && (
              <Link
                href={thirdBlog.link}
                className="relative flex-1 rounded-mob-6 sm:rounded-tab-6 lg:rounded-web-6 overflow-hidden block"
                onClick={() => setBlogId(thirdBlog.id)}
              >
                <div className="lg:max-h-[18vw] relative">
                  <Image
                    src={thirdBlog.image?.blogImage || '/images/company-news-banner-three.webp'}
                    alt={thirdBlog.image?.alt || thirdBlog.title}
                    fill
                    className="!static "
                    unoptimized={isExternalUrl(thirdBlog.image?.blogImage)}
                  />
                </div>
                <div className="absolute z-[5] w-full py-mob-14 sm:py-tab-16 lg:py-web-20 pl-mob-16 sm:pl-tab-16 lg:pl-web-22 pr-mob-32 sm:pr-tab-32 lg:pr-web-80 bottom-0 left-0 after:left-0 after:bottom-0 after:content-[''] after:absolute  bg-gradient-to-b from-transparent via-black/70 to-black after:z-[1] after:w-full after:h-full ">
                  <p
                    className="text-White text-mob-15 sm:text-tab-15 lg:text-web-22 font-medium lg:font-normal leading-[120%] relative z-[10] line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: thirdBlog.title }}
                  />
                </div>
              </Link>
            )}
          </div>
        </div>
        <Link href="/blogs" aria-label="Read all news and blogs from Gemini Building Materials">
          <Button variant="secondaryLinkBtnBlue">
            Read More
            <div className="w-mob-7 sm:w-tab-7 lg:w-web-7 h-auto">
              <Image
                src="/images/chevron-right-blue.svg"
                alt="Read More about Gemini Building Materials - UAE"
                aria-hidden="true"
                fill
                className="!static !h-auto"
              />
            </div>
          </Button>
        </Link>
      </div>
    </FadeInUp>
  );
}
